'use client'

import React, { useEffect, useState, useCallback } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { AspectRatio, Box, Spinner, BoxProps } from '@chakra-ui/react'
import Image from 'next/image'

interface SliderProps {
  list?: string[]
  borderRadius?: string
  maxHeight?: string
  aspectRatio?: number
  additionalStyles?: React.CSSProperties
  minH?: string
}

const Slider = ({
  list = [],
  borderRadius = '0',
  maxHeight = '280px',
  aspectRatio = 1,
  additionalStyles = {},
  minH = '',
}: SliderProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
  const [isLoading, setIsLoading] = useState(true)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  useEffect(() => {
    if (list.length > 0) {
      setIsLoading(false)
    }
  }, [list])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
  }, [emblaApi, onSelect])

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  if (isLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        height={minH ? minH : maxHeight}
      >
        <Spinner color='blue.500' size='lg' />
      </Box>
    )
  }

  return (
    <Box position='relative' {...(additionalStyles as BoxProps)}>
      <Box className='embla' overflow='hidden' ref={emblaRef}>
        <Box className='embla__container' display='flex'>
          {list.map((item, idx) => {
            if (!item) return null

            const baseUrl = process.env.BASE_URL || ''
            const imageUrl = item.includes(baseUrl) ? item : `${baseUrl}${item}`

            return (
              <Box
                key={`${item}-${idx}`}
                className='embla__slide'
                flexShrink={0}
                flexGrow={0}
                flexBasis='100%'
                minWidth='0'
              >
                <AspectRatio
                  ratio={aspectRatio}
                  maxH={maxHeight}
                  minH={minH}
                  overflow='hidden'
                  borderRadius={borderRadius}
                >
                  <Box position='relative' width='100%' height='100%'>
                    <Image
                      src={imageUrl}
                      alt={`Slide ${idx + 1}`}
                      fill
                      priority={idx === 0}
                      style={{
                        objectFit: 'cover',
                      }}
                      sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                    />
                  </Box>
                </AspectRatio>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export default Slider
